import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LearningResourceRequestReviewDialogComponent } from 'src/app/components/main/employees/rrhh/learning-resource-request-review-dialog/learning-resource-request-review-dialog.component';
import { LearningResourceRequestStatusEnum } from 'src/app/enums/learning-resource-request-status.enum';
import { LearningResourceRequestStatusEnumLabels } from 'src/app/enums/learning-resource-request-status.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResourceRequest, LearningResourceRequestSerialized } from 'src/app/models/learning_resource_request.model';
import { ApiService, UtilsService } from 'src/app/services';

@Component({
  selector: 'app-learning-resource-requests-list',
  templateUrl: './learning-resource-requests-list.component.html',
  styleUrls: ['./learning-resource-requests-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class LearningResourceRequestsListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  dataSource = new MatTableDataSource<LearningResourceRequestSerialized>([]);

  displayedColumns: string[] = ['user', 'title', 'price', 'status', 'created_at', 'actions'];

  readonly status_filter_options: { value: LearningResourceRequestStatusEnum | ''; label: string }[] = [
    { value: '', label: 'Todos' },
    {
      value: LearningResourceRequestStatusEnum.Pending,
      label: LearningResourceRequestStatusEnumLabels[LearningResourceRequestStatusEnum.Pending],
    },
    {
      value: LearningResourceRequestStatusEnum.Accepted,
      label: LearningResourceRequestStatusEnumLabels[LearningResourceRequestStatusEnum.Accepted],
    },
    {
      value: LearningResourceRequestStatusEnum.Declined,
      label: LearningResourceRequestStatusEnumLabels[LearningResourceRequestStatusEnum.Declined],
    },
  ];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
    status: [this.activated_route.snapshot.queryParamMap.get('status') ?? LearningResourceRequestStatusEnum.Pending],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.processUrlParams();

    const url_page = this.activated_route.snapshot.queryParamMap.get('page');
    if (!url_page) {
      this.pageIndex = 0;
    }

    super.ngOnInit();

    this.listenSearchFormControl();
    this.listenStatusFormControl();
  }

  openReviewDialog(learning_resource_request: LearningResourceRequestSerialized): void {
    const dialog_ref = this.mat_dialog.open(LearningResourceRequestReviewDialogComponent, {
      width: '560px',
      data: learning_resource_request,
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';
    const status = this.filterForm.get('status')?.value as LearningResourceRequestStatusEnum | '';

    const params: Record<string, unknown> = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
      order_by: 'created_at:desc',
      with: 'user',
    };

    if (status !== '') {
      params.status = status;
    }

    if (search_term) {
      params.scopes = `search:${search_term}`;
    }

    this.api_service.getLearningResourceRequestsPaged(params).subscribe({
      next: (response: ArrayResponse<LearningResourceRequest>): void => {
        this.dataSource.data = this.serializeLearningResourceRequests(response.data);
        this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
        this.change_detector_ref.markForCheck();
      },
    });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((search_term: string): void => {
        if (search_term !== '') {
          this.pageIndex = 0;
          this.fetchData();
        }
      });
  }

  private listenStatusFormControl(): void {
    this.filterForm
      .get('status')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.pageIndex = 0;
        this.fetchData();
      });
  }

  private processUrlParams(): void {
    const url_per_page = this.activated_route.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    const url_page = this.activated_route.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    }
  }

  private serializeLearningResourceRequests(learning_resource_requests: LearningResourceRequest[]): LearningResourceRequestSerialized[] {
    return learning_resource_requests.map(learning_resource_request => ({
      ...learning_resource_request,
      user_full_name: learning_resource_request.user
        ? `${learning_resource_request.user.name} ${learning_resource_request.user.surnames}`.trim()
        : '-',
      status_label: LearningResourceRequestStatusEnumLabels[learning_resource_request.status],
      is_pending: learning_resource_request.status === LearningResourceRequestStatusEnum.Pending,
    }));
  }
}
